import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Download,
  Filter,
  Users,
  Store,
  Calendar,
  Layers,
  CheckCircle,
  Clock,
  Printer as PrintIcon,
  RefreshCcw,
  Loader2,
  DollarSign,
  PieChart,
  Truck,
  Tag,
} from "lucide-react";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect, AsyncReactSelect } from "@/components/ui/react-select";

export const SupplierwiseExpensesReport = ({
  embedded = false,
  initialSupplierId = "",
  initialSupplierName = "",
  initialStoreId = "",
  onBackToList = null,
} = {}) => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const resolvedInitialStoreId = initialStoreId
    ? String(initialStoreId)
    : currentStore?.id
      ? String(currentStore.id)
      : "";

  const [filters, setFilters] = useState(() => ({
    supplier_id: initialSupplierId ? String(initialSupplierId) : "",
    store_id: resolvedInitialStoreId,
    category_id: "",
    start_date: "",
    end_date: "",
    min_amount: "",
    max_amount: "",
    limit: 100,
    sort_by: "total_amount",
    sort_order: "desc",
    status: "",
    po_status: "",
  }));
  const [selectedSupplier, setSelectedSupplier] = useState(
    initialSupplierId
      ? {
        value: String(initialSupplierId),
        label: initialSupplierName || "Selected Supplier",
      }
      : null,
  );
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);


  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const summaryStats = previewData?.summary || null;

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  // ==================== QUERIES ====================
  const loadSupplierOptions = async (inputValue) => {
    try {
      const params = { search: inputValue, perPage: 15 };
      if (filters.store_id) {
        params.store_id = filters.store_id;
      }

      const response = await apiClient.get(`${API_BASE_URL}/supplier`, {
        params,
      });
      const suppliersData = response.data.data || [];
      return suppliersData.map((sup) => ({
        value: String(sup.id),
        label: sup.company_name || sup.name || "Unknown Supplier",
      }));
    } catch (error) {
      console.error("Error loading suppliers:", error);
      return [];
    }
  };

  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report supplierwise expense view"),
    queryKey: ["stores-report"],
    queryFn: async () => {
      const response = await apiClient.get(`${API_BASE_URL}/store`);
      return response.data.data || response.data || [];
    },
  });

  const { data: categories = [], isFetching: isCategoriesFetching } = useQuery({
    enabled: hasPermission("report supplierwise expense view"),
    queryKey: ["expense-categories-report"],
    queryFn: async () => {
      const response = await apiClient.get(`${API_BASE_URL}/expense-categories`);
      return response.data.data || response.data || [];
    },
  });

  // ==================== MUTATIONS ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report supplierwise expense view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.supplier_id)
        params.supplier_id = currentFilters.supplier_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.category_id)
        params.category_id = currentFilters.category_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.min_amount !== "")
        params.min_amount = Number(currentFilters.min_amount);
      if (currentFilters.max_amount !== "")
        params.max_amount = Number(currentFilters.max_amount);
      if (currentFilters.limit) params.limit = Number(currentFilters.limit);
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;
      if (currentFilters.status && currentFilters.status !== "all")
        params.status = currentFilters.status;
      if (currentFilters.po_status && currentFilters.po_status !== "all")
        params.po_status = currentFilters.po_status;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.SUPPLIERWISE_EXPENSES,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report supplierwise expense view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.supplier_id)
        params.supplier_id = currentFilters.supplier_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.category_id)
        params.category_id = currentFilters.category_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.min_amount !== "")
        params.min_amount = Number(currentFilters.min_amount);
      if (currentFilters.max_amount !== "")
        params.max_amount = Number(currentFilters.max_amount);
      if (currentFilters.limit) params.limit = Number(currentFilters.limit);
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;
      if (currentFilters.status && currentFilters.status !== "all")
        params.status = currentFilters.status;
      if (currentFilters.po_status && currentFilters.po_status !== "all")
        params.po_status = currentFilters.po_status;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SUPPLIERWISE_EXPENSES,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast(
        "Supplierwise expenses report generated successfully!",
        "success",
      );
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate supplierwise expenses report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGenerateReport = () => {
    showToast("Generating supplierwise expenses report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    const cleared = {
      supplier_id: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
      category_id: "",
      start_date: "",
      end_date: "",
      min_amount: "",
      max_amount: "",
      limit: 100,
      sort_by: "total_amount",
      sort_order: "desc",
      status: "",
      po_status: "",
    };
    setFilters(cleared);
    setSelectedSupplier(null);
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `supplierwise-expense-report-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const storeOptions = [
    { value: "", label: "All Locations" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  const categoryOptions = categories.map((category) => ({
    value: String(category.id),
    label: category.name || category.title || "Unknown Category",
  }));

  const reportBody = (
    <div className="flex flex-col gap-6">
      {embedded && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl bg-card border shadow-sm">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold tracking-tight">Supplierwise Expenses Report</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Analyze expenses, purchase orders, and totals across suppliers.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {onBackToList && (
              <Button variant="outline" size="sm" onClick={onBackToList} className="rounded-lg">
                Back to Supplier List
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleClearFilters} className="rounded-lg">
              <RefreshCcw className="mr-1.5 h-4 w-4" /> Reset
            </Button>
            {hasPermission("report supplierwise expense view") && (
              <Button
                variant="secondary"
                size="sm"
                className="rounded-lg shadow-sm"
                onClick={handleGenerateReport}
                disabled={generateReportMutation.isPending}
              >
                {generateReportMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Download className="mr-1.5 h-4 w-4" /> Download PDF
                  </>
                )}
              </Button>
            )}
            {hasPermission("report supplierwise expense view") && (
              <Button
                size="sm"
                className="rounded-lg shadow-sm"
                onClick={() => {
                  showToast("Loading report data...", "info");
                  previewReportMutation.mutate(filters);
                }}
                disabled={previewReportMutation.isPending}
              >
                {previewReportMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Loading...
                  </>
                ) : (
                  <>
                    <FileText className="mr-1.5 h-4 w-4" /> Preview Report
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-8">
        {/* Main Filter Panel */}
        <Card className="shadow-none border border-border/50 overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-xl">Filtering Criteria</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Define the supplier, limits, and date range
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>
          {showFilters && (
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-8">
                {/* Date Scope */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" /> Date Scope
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Start Date
                      </Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="start_date"
                        value={filters.start_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">End Date</Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="end_date"
                        value={filters.end_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t space-y-4">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Layers className="h-4 w-4 text-primary" /> Sort & Limit
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Sort By</Label>
                        <Select
                          value={filters.sort_by}
                          onValueChange={(val) =>
                            setFilters((p) => ({ ...p, sort_by: val }))
                          }
                        >
                          <SelectTrigger className="bg-muted/30 border-border">
                            <SelectValue placeholder="Sort By" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="supplier_name">
                              Supplier Name
                            </SelectItem>
                            <SelectItem value="total_amount">
                              Total Amount
                            </SelectItem>
                            <SelectItem value="expense_count">
                              Expense Count
                            </SelectItem>
                            <SelectItem value="avg_expense">
                              Avg Expense
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          Sort Order
                        </Label>
                        <Select
                          value={filters.sort_order}
                          onValueChange={(val) =>
                            setFilters((p) => ({ ...p, sort_order: val }))
                          }
                        >
                          <SelectTrigger className="bg-muted/30 border-border">
                            <SelectValue placeholder="Sort Order" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Ascending</SelectItem>
                            <SelectItem value="desc">Descending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label className="text-muted-foreground">
                          Limit Results
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          className="bg-muted/30 border-border focus:bg-background transition-colors"
                          name="limit"
                          value={filters.limit}
                          onChange={handleFilterChange}
                          placeholder="Max 100"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Entity Filters */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Truck className="h-4 w-4 text-primary" /> Entity Scope
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Supplier</Label>
                      <AsyncReactSelect
                        loadOptions={loadSupplierOptions}
                        defaultOptions
                        value={selectedSupplier}
                        onChange={(opt) => {
                          setSelectedSupplier(opt);
                          setFilters((p) => ({
                            ...p,
                            supplier_id: opt?.value || "",
                          }));
                        }}
                        placeholder="Search Suppliers..."
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Store / Branch
                      </Label>
                      <ReactSelect
                        options={storeOptions}
                        value={
                          storeOptions.find(
                            (opt) => opt.value === String(filters.store_id || ""),
                          ) ||
                          (filters.store_id &&
                            currentStore?.id &&
                            String(filters.store_id) === String(currentStore.id)
                            ? {
                              value: String(currentStore.id),
                              label: currentStore.name,
                            }
                            : null)
                        }
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Locations"
                        isClearable={Boolean(filters.store_id)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Category</Label>
                      <ReactSelect
                        options={categoryOptions}
                        isLoading={isCategoriesFetching}
                        value={categoryOptions.find(
                          (opt) => opt.value === String(filters.category_id),
                        )}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            category_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Categories"
                        isClearable
                      />
                    </div>
                  </div>
                </div>

                {/* Amount & Status Filters */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Tag className="h-4 w-4 text-primary" /> Details & Status
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Status</Label>
                      <Select
                        value={filters.status}
                        onValueChange={(val) =>
                          setFilters((p) => ({
                            ...p,
                            status: val === "all" ? "" : val,
                          }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="pending_approval">
                            Pending Approval
                          </SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">PO Status</Label>
                      <Select
                        value={filters.po_status}
                        onValueChange={(val) =>
                          setFilters((p) => ({
                            ...p,
                            po_status: val === "all" ? "" : val,
                          }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue placeholder="All PO Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All PO Statuses</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="partial">Partial</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="canceled">Canceled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Min Amount
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="min_amount"
                        value={filters.min_amount}
                        onChange={handleFilterChange}
                        placeholder="e.g. 1000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Max Amount
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="max_amount"
                        value={filters.max_amount}
                        onChange={handleFilterChange}
                        placeholder="e.g. 50000"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {summaryStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total Suppliers
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {summaryStats.total_suppliers}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total Expenses
                    </p>
                    <h3 className="text-2xl font-bold text-primary">
                      {currencySymbol}
                      {(
                        parseFloat(summaryStats.total_expenses) || 0
                      ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total POs
                    </p>
                    <h3 className="text-2xl font-bold text-blue-600">
                      {currencySymbol}
                      {(parseFloat(summaryStats.total_pos) || 0).toLocaleString(
                        undefined,
                        { minimumFractionDigits: 2 },
                      )}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total Amount
                    </p>
                    <h3 className="text-2xl font-bold text-green-600">
                      {currencySymbol}
                      {(
                        parseFloat(summaryStats.total_amount) || 0
                      ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total Count
                    </p>
                    <h3 className="text-2xl font-bold text-orange-600">
                      {summaryStats.total_count}
                    </h3>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-4 flex flex-col justify-center">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Overall Avg
                    </p>
                    <h3 className="text-2xl font-bold text-purple-600">
                      {currencySymbol}
                      {(
                        parseFloat(summaryStats.overall_avg) || 0
                      ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </h3>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="shadow-lg border-none ring-1 ring-border/50 overflow-hidden">
              <CardHeader className="bg-muted/30 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="h-5 w-5 text-primary" />
                  Supplier Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableHead className="font-semibold text-foreground">
                          Supplier Name
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Expense Count
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Expense Total
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          PO Count
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          PO Total
                        </TableHead>
                        <TableHead className="font-semibold text-foreground text-right">
                          Total Amount
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData?.data?.length > 0 ? (
                        previewData.data.map((row, index) => (
                          <TableRow
                            key={index}
                            className="hover:bg-muted/30 transition-colors"
                          >
                            <TableCell className="font-medium">
                              {row.supplier_name}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.expense_count}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-muted-foreground">
                              {currencySymbol}
                              {(
                                parseFloat(row.expense_total) || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.po_count}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-muted-foreground">
                              {currencySymbol}
                              {(parseFloat(row.po_total) || 0).toLocaleString(
                                undefined,
                                { minimumFractionDigits: 2 },
                              )}
                            </TableCell>
                            <TableCell className="text-right font-bold text-foreground">
                              {currencySymbol}
                              {(
                                parseFloat(row.total_amount) || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="h-24 text-center text-muted-foreground"
                          >
                            No supplier data found for the selected criteria.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Generated!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  The Supplierwise Expenses Report is ready based on your
                  specific criteria and is waiting for your action.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (embedded) {
    return reportBody;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <FileText className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Financial Reporting
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Supplierwise Expenses Report
              </span>
            </div>
          }
          subtitle="Analyze your expenses across suppliers with advanced filtering and sorting."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            onClick={handleClearFilters}
            className="rounded-xl mr-2"
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report supplierwise expense view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report supplierwise expense view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Preview Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>
      {reportBody}
    </PageContainer>
  );
};

export default SupplierwiseExpensesReport;


